import type { ComponentAvailability, ComponentDefinition, MapId, ScenarioId } from "../domain/types";
import generatedHardware from "./wiki-hardware.generated.json";

export interface ComponentGroupDefinition {
    readonly id: string;
    readonly name: string;
    readonly parentId?: string;
    readonly color?: string;
    readonly componentIds: readonly string[];
}

interface GeneratedHardwareDefinition {
    readonly id: string;
    readonly name: string;
    readonly shortHandle: string;
    readonly category: string;
    readonly groupId: string;
    readonly sizeU: number;
    readonly imagePath: string;
    readonly wikiUrl: string;
    readonly userPlaceable: boolean;
    readonly availability: ComponentAvailability;
    readonly performance: ComponentDefinition["performance"];
}

interface GroupBlueprint {
    readonly id: string;
    readonly name: string;
    readonly parentId?: string;
    readonly color?: string;
}

const GROUP_BLUEPRINTS: readonly GroupBlueprint[] = Object.freeze([
    { id: "servers", name: "Servers" },
    { id: "servers-arm", name: "ARM Servers", parentId: "servers", color: "#6080A0" },
    { id: "servers-balanced", name: "Balanced Servers", parentId: "servers", color: "#3D7BFF" },
    { id: "servers-storage", name: "Storage Servers", parentId: "servers", color: "#FF8030" },
    { id: "servers-compute", name: "Compute Servers", parentId: "servers", color: "#E04848" },
    { id: "servers-memory", name: "Memory Optimised Servers", parentId: "servers", color: "#9D5DFF" },
    { id: "servers-gpu", name: "GPU Servers", parentId: "servers", color: "#3CC865" },
    { id: "servers-risc-v", name: "RISC-V Servers", parentId: "servers", color: "#27C4BF" },
    { id: "servers-mainframe", name: "Mainframes", parentId: "servers", color: "#555C61" },
    { id: "switches", name: "Switches" },
    { id: "switches-1g", name: "1G Switches", parentId: "switches", color: "#58748A" },
    { id: "switches-10g", name: "10G Switches", parentId: "switches", color: "#46718C" },
    { id: "switches-25g-40g", name: "25G / 40G Switches", parentId: "switches", color: "#315F7D" },
    { id: "switches-100g-plus", name: "100G+ Switches", parentId: "switches", color: "#274B65" },
    { id: "gateways", name: "Gateways", color: "#4B656F" },
    { id: "security", name: "Security" },
    { id: "security-gateways", name: "Security Gateways", parentId: "security", color: "#9DA3A8" },
    { id: "security-monitoring", name: "Security Monitoring", parentId: "security", color: "#B48848" },
    { id: "patch-panels", name: "Patch Panels", color: "#778692" },
    { id: "edd", name: "EDD", color: "#F09A72" },
]);

const sourceHardware = generatedHardware as readonly GeneratedHardwareDefinition[];

export const COMPONENT_GROUPS: readonly ComponentGroupDefinition[] = Object.freeze(
    GROUP_BLUEPRINTS.map((group) =>
        Object.freeze({
            ...group,
            componentIds: Object.freeze(
                sourceHardware.filter((component) => component.groupId === group.id).map((component) => component.id),
            ),
        }),
    ),
);

function findDirectGroup(componentId: string): ComponentGroupDefinition | undefined {
    return COMPONENT_GROUPS.find((group) => group.componentIds.includes(componentId));
}

function resolveGroupColor(componentId: string): string {
    let group = findDirectGroup(componentId);
    for (let step = 0; step < COMPONENT_GROUPS.length && group !== undefined; step += 1) {
        if (group.color !== undefined) {
            return group.color;
        }
        group = group.parentId === undefined ? undefined : COMPONENT_GROUPS.find((item) => item.id === group?.parentId);
    }
    throw new Error(`Component "${componentId}" must belong to a group with a color.`);
}

export function getComponentGroupPath(componentId: string): readonly ComponentGroupDefinition[] {
    const path: ComponentGroupDefinition[] = [];
    let group = findDirectGroup(componentId);
    for (let step = 0; step < COMPONENT_GROUPS.length && group !== undefined; step += 1) {
        path.unshift(group);
        group = group.parentId === undefined ? undefined : COMPONENT_GROUPS.find((item) => item.id === group?.parentId);
    }
    return path;
}

export function getComponentGroupSortIndex(componentId: string): number {
    const directGroup = findDirectGroup(componentId);
    if (directGroup === undefined) {
        return COMPONENT_GROUPS.length;
    }
    const groupIndex = COMPONENT_GROUPS.findIndex((group) => group.id === directGroup.id);
    return groupIndex < 0 ? COMPONENT_GROUPS.length : groupIndex;
}

export const COMPONENT_CATALOG: readonly ComponentDefinition[] = Object.freeze(
    sourceHardware.map((component) =>
        Object.freeze({
            id: component.id,
            name: component.name,
            shortHandle: component.shortHandle,
            category: component.category,
            sizeU: component.sizeU,
            color: resolveGroupColor(component.id),
            imagePath: component.imagePath,
            wikiUrl: component.wikiUrl,
            userPlaceable: component.userPlaceable,
            availability: Object.freeze({ ...component.availability }),
            performance: Object.freeze({ ...component.performance }),
        }),
    ),
);

export const USER_PLACEABLE_COMPONENTS: readonly ComponentDefinition[] = Object.freeze(
    COMPONENT_CATALOG.filter((component) => component.userPlaceable !== false),
);

export function getComponent(componentId: string): ComponentDefinition | undefined {
    return COMPONENT_CATALOG.find((component) => component.id === componentId);
}

export function getComponents(): readonly ComponentDefinition[] {
    return COMPONENT_CATALOG;
}

export function getUserPlaceableComponents(): readonly ComponentDefinition[] {
    return USER_PLACEABLE_COMPONENTS;
}

export function isComponentAvailable(component: ComponentDefinition, scenarioId: ScenarioId, mapId: MapId): boolean {
    const configuredMaps = component.availability[scenarioId];
    if (scenarioId === "sandbox" && configuredMaps === undefined) {
        return true;
    }
    return configuredMaps?.includes(mapId) === true;
}

export function getAvailableUserPlaceableComponents(
    scenarioId: ScenarioId,
    mapId: MapId,
): readonly ComponentDefinition[] {
    return USER_PLACEABLE_COMPONENTS.filter((component) => isComponentAvailable(component, scenarioId, mapId));
}

export function getComponentGroups(): readonly ComponentGroupDefinition[] {
    return COMPONENT_GROUPS;
}

export function getUserPlaceableCategories(): readonly string[] {
    return USER_PLACEABLE_CATEGORIES;
}

export const CATALOG_CATEGORIES = Object.freeze([...new Set(COMPONENT_CATALOG.map((component) => component.category))]);
export const USER_PLACEABLE_CATEGORIES = Object.freeze([
    ...new Set(USER_PLACEABLE_COMPONENTS.map((component) => component.category)),
]);
